/**
 * Generate FAQ JSON-LD for the AI Access Checker page
 * Used for SEO/structured data
 *
 * This is a server-safe function (no "use client" directive)
 */
export function generateAiAccessCheckerFaqJsonLd() {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: [
			{
				"@type": "Question",
				name: "What does this tool check?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "This tool analyzes a website's robots.txt file to determine if AI crawlers and chatbots are blocked from accessing the content. It checks for restrictions against AI visitors like ChatGPT, Claude, and Perplexity, as well as AI model trainers like GPTBot and Google-Extended.",
				},
			},
			{
				"@type": "Question",
				name: "Why does AI access matter for SEO and PR?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "AI assistants like ChatGPT, Claude, and Perplexity are increasingly used by consumers to research products and services. If your content is blocked by robots.txt, these AI systems cannot cite your website in their responses, potentially reducing your brand's visibility and the value of backlinks pointing to your site.",
				},
			},
			{
				"@type": "Question",
				name: "What's the difference between red, yellow, and green status?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "Red means some AI assistants can't open the site's pages live while they answer, because AI Visitors such as ChatGPT-User are blocked. Yellow means the site blocks AI search crawlers such as OAI-SearchBot, AI model trainers such as GPTBot, or both. A search crawler block may leave the site out of that assistant's search results, though many sites like this are still cited. A trainer block affects future AI models, not today's answers. Green means none of the AI bots we check are blocked. If we can't load the robots.txt file at all, we say we couldn't check instead of guessing.",
				},
			},
			{
				"@type": "Question",
				name: "How does this affect the value of my backlinks?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "If a site blocks AI crawlers, any backlink you earn from that site may have reduced value in AI-driven search and recommendations. AI assistants won't be able to discover or cite content from sites with restrictive robots.txt policies, limiting the reach of your earned media placements.",
				},
			},
			{
				"@type": "Question",
				name: "Should I block AI crawlers on my site?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "It depends on your goals. Blocking AI model trainers can prevent your content from being used to train future AI models, but blocking AI visitors prevents your content from being cited in AI-generated responses. Most brands benefit from allowing AI visitors to maximize visibility while potentially restricting model trainers.",
				},
			},
			{
				"@type": "Question",
				name: "How do I fix robots.txt issues?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "To allow AI crawlers, you need to modify your robots.txt file to remove or adjust User-agent rules that block them. This typically involves allowing user agents like GPTBot, ChatGPT-User, Claude-Web, and PerplexityBot. See our detailed guide for specific instructions.",
				},
			},
			{
				"@type": "Question",
				name: "What if no robots.txt is found?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "If a website has no robots.txt file, all crawlers (including AI bots) have unrestricted access. This is actually good for AI visibility - it means your content can be discovered and cited by AI assistants. However, you may want to create a robots.txt to have more control over which bots can access your site.",
				},
			},
			{
				"@type": "Question",
				name: "Can I track when AI bots visit my site?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "Yes! Spyglasses AI Traffic Analytics can detect and log visits from AI crawlers in real-time. You can see which AI bots are visiting your site, how often, and what content they're accessing. You can even use our traffic control features to selectively block or allow specific bots without modifying your robots.txt.",
				},
			},
		],
	};
}
